import { useState } from 'react'

import { api } from '../api'
import { MONATE_KURZ, datum, heuteIso, tonnen, zahlLesen } from '../format'
import { useAktion } from '../hooks'
import { RECHENBASEN } from '../typen'
import type { Rechenbasis, ReichweiteErgebnis } from '../typen'
import ArtikelAuswahl from './ArtikelAuswahl'
import Meldung from './Meldung'
import Meldungen from './Meldungen'

/**
 * „Was wäre, wenn" (02.11.2026): wie lange reicht ein Material mit einem angenommenen Bestand und Zugängen an
 * bestimmten Tagen - „zum 25.10. noch 729 t, plus 900 t am 15.01.". Gerechnet wird im Backend mit derselben
 * Monatskurve wie das Blatt (`POST /api/disposition/reichweite`); gespeichert wird nichts. Bisher ging das nur
 * über den Assistenten und eigenes SQL (Frau Barth, Oktober 2026).
 *
 * Ein Abschnitt wie die Pflegeabschnitte darunter: zu eine Zeile, auf ein kleines Formular.
 */

interface Eigenschaften {
  /** die Materialien des Blatts - nur sie stehen zur Wahl */
  materialien: { artikelnummer: string; bezeichnung: string }[]
  /** die Rechenbasis der Ansicht; leer = die Einstellung */
  rechenbasis: Rechenbasis | ''
}

interface Zugangszeile {
  tag: string
  t: string
}

/** „KW 12/27 (20.03.2027)" - oder „nicht absehbar", wenn die Rechnung kein Ende fand. */
function ende(kw: string | null | undefined, tag: string | null | undefined): string {
  if (!tag) return 'nicht absehbar (Bestand reicht über den gerechneten Zeitraum)'
  return kw ? `${kw} (${datum(tag)})` : datum(tag)
}

/** „2026-11" → „Nov 2026" */
function monatText(monat: string): string {
  const [jahr, m] = monat.split('-')
  return `${MONATE_KURZ[Number(m) - 1] ?? m} ${jahr}`
}

export default function ReichweiteRechnen({ materialien, rechenbasis }: Eigenschaften) {
  const [offen, setOffen] = useState(false)
  const [material, setMaterial] = useState('')
  const [bestand, setBestand] = useState('')
  const [ab, setAb] = useState('')
  const [zugaenge, setZugaenge] = useState<Zugangszeile[]>([{ tag: '', t: '' }])
  const [bestandAm, setBestandAm] = useState('')
  const [basis, setBasis] = useState<Rechenbasis | '' | null>(null)
  const [ergebnis, setErgebnis] = useState<ReichweiteErgebnis | null>(null)
  const { fehler, arbeitet, setFehler, ausfuehren } = useAktion()
  // Solange niemand hier eine Rechenbasis wählt, gilt die der Ansicht darüber.
  const gewaehlteBasis = basis ?? rechenbasis
  const gewaehlt = material && materialien.some((m) => m.artikelnummer === material) ? material : (materialien[0]?.artikelnummer ?? '')

  function zugangSetzen(index: number, feld: Partial<Zugangszeile>) {
    setZugaenge((alt) => alt.map((z, i) => (i === index ? { ...z, ...feld } : z)))
  }

  function rechnen() {
    const fehlerliste: string[] = []
    const bestandT = bestand.trim() ? zahlLesen(bestand) : null
    if (bestand.trim() && (bestandT === null || bestandT < 0)) fehlerliste.push(`Bestand: „${bestand.trim()}“ ist keine Menge in Tonnen.`)
    if (ab && ab > heuteIso() && bestandT === null) fehlerliste.push('Für einen Tag in der Zukunft den angenommenen Bestand angeben.')
    const liste: { tag: string; t: number }[] = []
    for (const z of zugaenge) {
      if (!z.tag && !z.t.trim()) continue
      const t = zahlLesen(z.t)
      if (!z.tag || t === null || t <= 0) {
        fehlerliste.push(`Zugang ${z.tag ? datum(z.tag) : 'ohne Tag'}: Tag und Menge in Tonnen (über 0) angeben.`)
        continue
      }
      liste.push({ tag: z.tag, t })
    }
    if (!gewaehlt) fehlerliste.push('Material wählen.')
    if (fehlerliste.length > 0) {
      setFehler(fehlerliste.join(' '))
      return
    }
    setErgebnis(null)
    void ausfuehren(async () => {
      setErgebnis(
        await api.reichweiteRechnen({
          material: gewaehlt,
          bestand_t: bestandT,
          ab: ab || null,
          zugaenge: liste,
          rechenbasis: gewaehlteBasis || null,
          bestand_am: bestandAm || null,
        }),
      )
    })
  }

  return (
    <div className="karte pflege">
      <div className="leiste pflege-kopf">
        <b>Was wäre, wenn</b>
        <span className="hinweis">Reichweite mit angenommenem Bestand oder Zugängen rechnen - ändert nichts</span>
        <button onClick={() => setOffen((o) => !o)}>{offen ? 'Schließen' : 'Öffnen'}</button>
      </div>
      {offen && (
        <div className="pflege-formular">
          <p className="hinweis">
            Dieselbe Monatskurve wie das Blatt. Bestand leer: der gebuchte Bestand bis zum Tag „ab“, abzüglich gesperrter Ware.
          </p>
          <div className="leiste felder">
            <label className="feld">
              <span className="hinweis">Material</span>
              <ArtikelAuswahl artikel={materialien} wert={gewaehlt} onChange={setMaterial} />
            </label>
            <label className="feld">
              <span className="hinweis">Bestand (t)</span>
              <input
                type="text"
                inputMode="decimal"
                style={{ width: 90, textAlign: 'right' }}
                placeholder="gebucht"
                value={bestand}
                onChange={(e) => setBestand(e.target.value)}
              />
            </label>
            <label className="feld">
              <span className="hinweis">ab (leer = heute)</span>
              <input type="date" value={ab} onChange={(e) => setAb(e.target.value)} />
            </label>
            <label className="feld">
              <span className="hinweis">Bestand ausrechnen am</span>
              <input type="date" value={bestandAm} onChange={(e) => setBestandAm(e.target.value)} />
            </label>
            <label className="feld">
              <span className="hinweis">Rechenbasis</span>
              <select value={gewaehlteBasis} onChange={(e) => setBasis(e.target.value as Rechenbasis | '')}>
                <option value="">wie eingestellt</option>
                {RECHENBASEN.map((r) => (
                  <option key={r.wert} value={r.wert}>
                    {r.titel}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="leiste felder">
            {zugaenge.map((z, i) => (
              <span key={i} className="leiste" style={{ marginBottom: 0 }}>
                <label className="feld">
                  <span className="hinweis">Zugang am</span>
                  <input type="date" value={z.tag} onChange={(e) => zugangSetzen(i, { tag: e.target.value })} />
                </label>
                <label className="feld">
                  <span className="hinweis">Menge (t)</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    style={{ width: 80, textAlign: 'right' }}
                    value={z.t}
                    onChange={(e) => zugangSetzen(i, { t: e.target.value })}
                  />
                </label>
                {zugaenge.length > 1 && (
                  <button className="leise" onClick={() => setZugaenge((alt) => alt.filter((_, j) => j !== i))}>
                    entfernen
                  </button>
                )}
              </span>
            ))}
            <button className="leise" onClick={() => setZugaenge((alt) => [...alt, { tag: '', t: '' }])}>
              weiterer Zugang
            </button>
            <button className="haupt" disabled={arbeitet || materialien.length === 0} onClick={rechnen}>
              {arbeitet ? 'Rechnet…' : 'Rechnen'}
            </button>
          </div>
          <Meldungen fehler={fehler} />
          {ergebnis && <Ergebnis e={ergebnis} />}
        </div>
      )}
    </div>
  )
}

/** Das Ergebnis in Worten, darunter der Monatsverlauf. */
function Ergebnis({ e }: { e: ReichweiteErgebnis }) {
  const basisTitel = RECHENBASEN.find((r) => r.wert === e.rechenbasis)?.titel ?? e.rechenbasis
  const engpaesse = e.engpaesse ?? []
  return (
    <>
      {e.datenstand_hinweis && (
        <Meldung art="warnung" titel="Datenstand">
          {e.datenstand_hinweis}
        </Meldung>
      )}
      <Meldung art={engpaesse.length > 0 ? 'warnung' : 'hinweis'} titel={`${e.material} ${e.bezeichnung}`}>
        <p style={{ margin: '0 0 4px' }}>
          Mit {tonnen(e.bestand_t)} t ab {datum(e.ab)} ({e.bestand_quelle}) reicht der Bestand bis <b style={{ display: 'inline' }}>{ende(e.reicht_kw, e.reicht_bis)}</b>
          {e.massgeblich_quelle ? ` - maßgeblich: ${e.massgeblich_quelle}` : ''}.
        </p>
        <div className="hinweis">
          Ist-Verbrauch: {ende(e.reicht_bis_ist_kw, e.reicht_bis_ist)} · Forecast GF: {ende(e.reicht_bis_forecast_kw, e.reicht_bis_forecast)} ·
          Rechenbasis: {basisTitel}
          {e.verbrauch_je_monat_t != null ? ` · Ø ${tonnen(e.verbrauch_je_monat_t)} t je Monat (nächste drei Monate)` : ''}
        </div>
        {engpaesse.map((g, i) => (
          <div key={i}>
            Engpass: leer ab {datum(g.leer_ab)}, der nächste Zugang kommt erst am {datum(g.zugang_am)}.
          </div>
        ))}
        {e.ende_nach_zugaengen && e.ende_nach_zugaengen !== e.reicht_bis && (
          <div>Mit allen Zugängen aufgebraucht am {datum(e.ende_nach_zugaengen)}.</div>
        )}
        {e.bestand_am && e.bestand_am_t != null && (
          <div>
            Bestand am {datum(e.bestand_am)}: <b style={{ display: 'inline' }}>{tonnen(e.bestand_am_t)} t</b>
            {e.bestand_am_t <= 0 ? ' - dann ist nichts mehr da.' : '.'}
          </div>
        )}
        {e.trend_hinweis && (
          <div>
            <span className="marke warnung">Trend</span> {e.trend_hinweis}
          </div>
        )}
      </Meldung>
      {(e.verlauf ?? []).length > 0 && (
        <div className="tabellenrahmen">
          <table>
            <thead>
              <tr>
                <th>Monat</th>
                <th className="zahl">Bedarf t</th>
                <th className="zahl">Zugang t</th>
                <th className="zahl">Bestand Monatsende t</th>
              </tr>
            </thead>
            <tbody>
              {(e.verlauf ?? []).map((m) => (
                <tr key={m.monat}>
                  <td>{monatText(m.monat)}</td>
                  <td className="zahl">{tonnen(m.bedarf_t)}</td>
                  <td className="zahl">{m.zugang_t ? tonnen(m.zugang_t) : ''}</td>
                  <td className={`zahl${m.bestand_ende_t <= 0 ? ' unter-mindestbestand' : ''}`}>
                    {tonnen(m.bestand_ende_t)}
                    {m.bestand_ende_t <= 0 ? ' leer' : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
